/**
 * Header — THE app header, for every role.
 *
 * There were once three of these (Header / HrHeader / MemberHeader). They drifted
 * into three different DOM trees against one stylesheet, so each role got a
 * different-looking nav: the CSS in features/backgrounds/backgrounds.css styles
 * `.logo-wrap`, `.menu-inner` and `.admin-settings` as floating glass pills, and
 * whether the logo sat inside `.menu-inner` or beside it changed the whole
 * layout. Admin/HR/Member now all render this one component, so the header
 * cannot diverge by role again.
 *
 * Role only ever affects DATA (which menu items Menu resolves, where Settings
 * navigates) — never structure or styling.
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Menu from "./Menu";
import { logout } from "../api/auth";
import NotificationBell from "../features/notifications/NotificationBell";
import ProfileChip from "./ProfileChip";
import ThemeToggle from "../theme/ThemeToggle";
import { shouldPlayIntro, disarmIntro } from "../motion/introMotion";

import UserAvatar from "./UserAvatar";
import emlogo from "../images/em-logo.svg";
import Icon from "../components/em/Icon";

const toggleBodyClass = () => {
  document.body.classList.toggle("menu-active");
};
const handleNavClick = () => {
  document.body.classList.remove("menu-active");
};
const getUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("userdata") || "{}");
  } catch {
    return {};
  }
};
const Header = ({ onboarding = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username") || "Admin"; // <— dynamic user name
  // Update getUserData usage to also grab email
  const [userInfo, setUserInfo] = useState(() => {
    const d = getUserData();
    const fullName =
      `${d.first_name || ""} ${d.last_name || ""}`.trim() ||
      d.username ||
      "Member";
    const designation = d.jobtitle || d.designation || "Member";
    const email = d.email || localStorage.getItem("email") || "";
    // Display only. `role` is the canonical routing role, and every custom role
    // collapses to "member" there; `role_display` is the role's real name
    // (e.g. "Employee", "Team Lead") as the server sends it on login/refresh.
    const role =
      d.role_display || d.role || localStorage.getItem("role") || "Member";
    return { fullName, designation, email, role };
  });
  const [active, setActive] = useState("dashboard");
  const [showDropdown, setShowDropdown] = useState(false);
  const settingsRef = useRef(null);
  // Post-login intro: drop + settle, items cascading left to right. Captured
  // once at mount; the safety timer disarms even if no dashboard grid does
  // (e.g. accounts that land on a non-dashboard page).
  const [introPlay] = useState(() => shouldPlayIntro());
  useEffect(() => {
    if (!introPlay) return undefined;
    const t = setTimeout(disarmIntro, 4000);
    return () => clearTimeout(t);
  }, [introPlay]);
  useEffect(() => {
    if (location.state?.active) {
      setActive(location.state.active);
    }
  }, [location.state]);
  useEffect(() => {
    setShowDropdown(false);
  }, [location.pathname]);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Live-sync when the profile page saves changes, so the name in the drawer and
  // the profile chip update without a reload.
  useEffect(() => {
    const onProfileUpdated = (e) => {
      const d = e.detail || {};
      const fullName =
        `${d.first_name || ""} ${d.last_name || ""}`.trim() ||
        getUserData().username ||
        "Member";
      const designation =
        d.jobtitle || d.designation || getUserData().designation || "Member";
      // Merge, don't replace: the payload carries no role, and dropping it would
      // blank the role badge — the only label on the collapsed chip.
      setUserInfo((prev) => ({ ...prev, fullName, designation }));
    };
    window.addEventListener("profileUpdated", onProfileUpdated);
    return () => window.removeEventListener("profileUpdated", onProfileUpdated);
  }, []);

  const handleLogout = () => {
    // Server-side revoke + clear local state + redirect. The hardLogout
    // path inside ./api/auth handles all of this so we don't have to
    // remember to clear individual keys.
    logout();
  };
  const handleSettings = () => {
    const base =
      role === "Admin" ? "/admin" : role === "HR" ? "/hr" : "/member";
    navigate(`${base}/settings`);
    setShowDropdown(false);
    document.body.classList.remove("menu-active");
  };
  // Profile navigation now lives in ProfileChip, so every header opens the same
  // page from the same code.

  const initials = userInfo.fullName
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

  return (
    <div className={`main-header${introPlay ? " hdr-intro" : ""}`}>
      <div className="header-right">
        <div
          className={`menu-wrap ${onboarding ? "opacity-50 pointer-events-none" : ""}`}
        >
          {/* Tapping the dimmed backdrop closes the drawer (mobile only). */}
          <div
            className="menu-backdrop"
            onClick={() => document.body.classList.remove("menu-active")}
            aria-hidden="true"
          />
          <div className="menu-inner">
            <div className="logo-wrap">
              <div className="logo">
                <img src={emlogo} alt="Logo" />
              </div>
            </div>
            {/* Drawer-only profile header (hidden on desktop) */}
            <div className="mob-drawer mob-drawer-head">
              <UserAvatar initials={initials} />
              <div className="mob-drawer-id">
                <b>{userInfo.fullName}</b>
                <small>{userInfo.designation}</small>
              </div>
              <span className="mob-role" style={{ textTransform: "capitalize" }}>
                {userInfo.role}
              </span>
            </div>

            <Menu onNavClick={handleNavClick} />

            {/* Drawer-only actions: theme, settings, logout (hidden on desktop) */}
            {!onboarding && (
              <div className="mob-drawer mob-drawer-actions">
                <div className="mob-act-theme">
                  <ThemeToggle />
                  <span>Switch theme</span>
                </div>
                <button
                  type="button"
                  className="mob-act-btn"
                  onClick={handleSettings}
                >
                  <i className="icon-wrap">
                    <Icon name="settings" />
                  </i>
                  Settings
                </button>
                <button
                  type="button"
                  className="mob-act-btn"
                  onClick={handleLogout}
                >
                  <i className="icon-wrap">
                    <Icon name="logout" />
                  </i>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
        {!onboarding && (
          <>
            <div className="admin-settings">
              <ul>
                {/* <li className="hdr-li hdr-theme">
                  <ThemeToggle />
                </li> */}
                <li className="hdr-li hdr-notif">
                  <NotificationBell />
                </li>
                <li
                  className="hdr-li hdr-settings"
                  ref={settingsRef}
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <i className="setti-icon">
                    <Icon name="settings" size={20} />
                  </i>
                  {showDropdown && (
                    <div className="dropdown-menu">
                      <ul>
                        <li>
                          <a onClick={handleSettings}>
                            <i className="icon-wrap">
                              <Icon name="settings" />
                            </i>
                            Settings
                          </a>
                        </li>
                        <li>
                          <a onClick={handleLogout}>
                            <i className="icon-wrap">
                              <Icon name="logout" />
                            </i>
                            Logout
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </li>
                <li className="hdr-li hdr-burger">
                  <div
                    className="mob-menu"
                    onClick={toggleBodyClass}
                    role="button"
                    aria-label="Toggle menu"
                  >
                    <hr />
                    <hr />
                    <hr />
                  </div>
                </li>
              </ul>

              <ProfileChip fullName={userInfo.fullName} role={userInfo.role} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
